import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Badge} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/badge.types';
import {PriceAndBrandSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useMemo} from 'react';

export const useGetBadgesFromBulkRequest = ({
  imagesSectionBadges,
  priceAndBrand,
}: {
  imagesSectionBadges?: Badge[];
  priceAndBrand?: PriceAndBrandSection;
}) => {
  const {isActive: isPriceOverhaulV2Active} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );

  const priceAndBrandBadges = useMemo(() => {
    if (!priceAndBrand) {
      return [];
    }

    if (isPriceOverhaulV2Active && priceAndBrand.priceV2) {
      return priceAndBrand?.priceV2.badge
        .map(badge => ({
          tracker: badge.tracker,
          type: {
            $case: 'sale',
            sale: {
              defaultLabel: badge.text,
            },
          },
        }))
        .filter(Boolean) as Badge[];
    }

    if (priceAndBrand.price && priceAndBrand.price.priceTags.length > 0) {
      return priceAndBrand.price.priceTags
        .map(priceTag => {
          if (priceTag.type?.$case === 'badge') {
            return {
              tracker: {
                contextKey: priceTag.type.badge.tracker.contextKey,
              },
              type: {
                $case: 'sale',
                sale: {
                  defaultLabel: priceTag.type.badge.text,
                },
              },
            };
          }

          return null;
        })
        .filter(Boolean) as Badge[];
    }

    const saleBadges = priceAndBrand.price
      ? (priceAndBrand.price.reductions
          .map(reduction => ({
            tracker: reduction.badge?.tracker,
            type: {
              $case: 'sale',
              sale: {
                defaultLabel: reduction.badge?.text,
              },
            },
          }))
          .filter(Boolean) as Badge[])
      : [];

    return saleBadges;
  }, [isPriceOverhaulV2Active, priceAndBrand]);

  const combinedBadges = useMemo(
    () => [...(imagesSectionBadges ?? []), ...priceAndBrandBadges],
    [imagesSectionBadges, priceAndBrandBadges],
  );

  return combinedBadges;
};
